import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { DollarSign, Plus, Trash2, TrendingUp, Activity, AlertTriangle, Clock, X, Save } from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";

export default function BillingPage() {
    const [cycles, setCycles] = useState([]);
    const [patients, setPatients] = useState([]);
    const [ind, setInd] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        patient_id: "", model: "bundle_payment", label: "", start_date: "", end_date: "", amount: 0, notes: "",
    });

    const load = async () => {
        const [c, p, i] = await Promise.all([
            api.get("/billing/cycles"),
            api.get("/patients"),
            api.get("/billing/indicators"),
        ]);
        setCycles(c.data); setPatients(p.data); setInd(i.data);
        if (p.data.length > 0 && !form.patient_id) setForm((f) => ({ ...f, patient_id: p.data[0].id }));
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/billing/cycles", { ...form, amount: Number(form.amount) });
            toast.success("Ciclo criado");
            setShowForm(false);
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (id) => {
        await api.delete(`/billing/cycles/${id}`);
        toast.success("Ciclo removido"); await load();
    };

    const brl = (n) => (n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

    const cards = ind ? [
        { icon: Activity, label: "Pacientes ativos", value: ind.total_patients },
        { icon: TrendingUp, label: "Consultas realizadas", value: ind.completed_consultations, sub: `${ind.total_consultations} total` },
        { icon: AlertTriangle, label: "Pós-desospitalização", value: ind.post_hospitalization_count, sub: "casos ativos" },
        { icon: AlertTriangle, label: "Pacientes com histórico de quedas", value: ind.falls_patients, accent: true },
        { icon: Clock, label: "Média até 1ª consulta", value: ind.avg_days_to_first_consultation == null ? "—" : `${ind.avg_days_to_first_consultation}d` },
        { icon: DollarSign, label: "Total contratado", value: brl(cycles.reduce((s, c) => s + (c.amount || 0), 0)) },
    ] : [];

    return (
        <AppShell>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--brand-secondary)] mb-1">Bundle Payment · Capitation</div>
                    <h1 className="font-display text-3xl font-medium text-brand">Faturamento e indicadores</h1>
                </div>
                <button data-testid="new-cycle-btn" onClick={() => setShowForm(true)} className="btn-primary"><Plus size={16} /> Novo ciclo</button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                {cards.map((c, i) => {
                    const Icon = c.icon;
                    return (
                        <div key={i} className={`card-earthy ${c.accent ? "border-l-4 border-[var(--brand-accent)]" : ""}`}>
                            <div className="flex items-start justify-between mb-2">
                                <Icon size={18} className="text-[var(--brand-secondary)]" />
                                <span className="text-xs text-muted-clinical uppercase tracking-wider text-right">{c.label}</span>
                            </div>
                            <div className="font-display text-2xl font-semibold text-brand">{c.value}</div>
                            {c.sub && <div className="text-xs text-muted-clinical mt-1">{c.sub}</div>}
                        </div>
                    );
                })}
            </div>

            {showForm && (
                <div className="card-earthy mb-6 relative">
                    <button onClick={() => setShowForm(false)} className="absolute top-3 right-3 text-muted-clinical hover:text-brand"><X size={16} /></button>
                    <h3 className="font-display text-lg font-medium text-brand mb-3">Novo ciclo de faturamento</h3>
                    <form onSubmit={submit} className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="label-clinical">Paciente</label>
                            <select required value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} className="input-clinical">
                                {patients.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-clinical">Modelo</label>
                            <select value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="input-clinical">
                                <option value="bundle_payment">Bundle Payment</option>
                                <option value="capitation">Capitation</option>
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="label-clinical">Descrição do ciclo</label>
                            <input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input-clinical" placeholder="Ex.: Pacote pós-alta 90 dias" />
                        </div>
                        <div>
                            <label className="label-clinical">Início</label>
                            <input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="input-clinical" />
                        </div>
                        <div>
                            <label className="label-clinical">Fim</label>
                            <input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="input-clinical" />
                        </div>
                        <div>
                            <label className="label-clinical">Valor (R$)</label>
                            <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-clinical" />
                        </div>
                        <div>
                            <label className="label-clinical">Observações</label>
                            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-clinical" />
                        </div>
                        <div className="md:col-span-2">
                            <button data-testid="save-cycle-btn" className="btn-primary"><Save size={16} /> Salvar ciclo</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="card-earthy">
                <h3 className="font-display text-lg font-medium text-brand mb-3">Ciclos contratados</h3>
                {cycles.length === 0 ? (
                    <div className="text-sm text-muted-clinical">Nenhum ciclo cadastrado.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase tracking-wider text-muted-clinical text-left">
                                    <th className="py-2">Paciente</th><th>Modelo</th><th>Descrição</th><th>Período</th><th className="text-right">Consultas</th><th className="text-right">Valor</th><th></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[rgba(30,63,51,0.08)]">
                                {cycles.map((c) => (
                                    <tr key={c.id} data-testid={`cycle-row-${c.id}`}>
                                        <td className="py-3 font-medium text-brand">{c.patient_name}</td>
                                        <td><span className="text-xs px-2 py-0.5 rounded-full bg-alt text-brand-secondary">{c.model === "bundle_payment" ? "Bundle" : "Capitation"}</span></td>
                                        <td className="text-[var(--text-secondary)]">{c.label}</td>
                                        <td className="text-xs text-muted-clinical">{c.start_date} → {c.end_date}</td>
                                        <td className="text-right">{c.consultation_count}</td>
                                        <td className="text-right font-medium">{brl(c.amount)}</td>
                                        <td className="text-right"><button onClick={() => remove(c.id)} className="text-[var(--status-danger)] p-1 hover:bg-[var(--status-danger-bg)] rounded"><Trash2 size={14} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AppShell>
    );
}
